import { Icon } from '@mui/material';
import { useEffect, useState } from 'react';
import { FlightStatus } from '../../../common/enums';
import { queryDashboardCounts } from '../../../services/grid-query-service';
import { useAppSelector } from '../../hooks/store-hooks';
import { buildDashboardSummaryFilters, DASHBOARD_COUNT_INDEX, dashboardFlightFilters } from '../../utils/dashboard';
import { dashboardCategoryIcons } from './dashboard-category-icons';
import './Dashboard.css';

const summaryFilters = buildDashboardSummaryFilters(dashboardFlightFilters, {
    planned: FlightStatus.Planned,
    scheduled: FlightStatus.Scheduled
});
const PLANNED_COUNT_INDEX = DASHBOARD_COUNT_INDEX.monitoredPlanned;

const displayCategories: {
    id: keyof typeof dashboardFlightFilters;
    label: string;
    countIndex: number;
}[] = [
    { id: 'monitored', label: 'Monitored flights', countIndex: DASHBOARD_COUNT_INDEX.monitoredActive },
    { id: 'holding', label: 'Holding', countIndex: DASHBOARD_COUNT_INDEX.holding },
    { id: 'diverting', label: 'Diverting', countIndex: DASHBOARD_COUNT_INDEX.diverting },
    { id: 'emergency', label: 'Emergency', countIndex: DASHBOARD_COUNT_INDEX.emergency },
    { id: 'go-around', label: 'Go-around', countIndex: DASHBOARD_COUNT_INDEX.goAround },
    { id: 'taxi-times', label: 'Taxi times', countIndex: DASHBOARD_COUNT_INDEX.taxiTimes },
    { id: 'position-updated', label: 'Late pos update', countIndex: DASHBOARD_COUNT_INDEX.positionUpdated }
];

const formatCount = (count: number | undefined) => count?.toLocaleString() ?? '—';

interface DashboardSummaryProps {
    onCountsChange?: (counts: number[]) => void;
}

export default function DashboardSummary({ onCountsChange }: DashboardSummaryProps) {
    const focusedWorkspaceId = useAppSelector((state) => state.workspace.selectedId);
    const lastUpdate = useAppSelector((state) => state.global.flightWorker.lastUpdate?.time);
    const [data, setData] = useState<number[]>([]);

    useEffect(() => {
        let cancelled = false;

        queryDashboardCounts(summaryFilters)
            .then((counts) => {
                if (!cancelled) {
                    onCountsChange?.(counts);
                    setData((previous) =>
                        previous.length === counts.length && previous.every((count, index) => count === counts[index]) ? previous : counts
                    );
                }
            })
            .catch((error) => {
                if (!cancelled) console.error('Unable to refresh dashboard summary', error);
            });

        return () => {
            cancelled = true;
        };
    }, [focusedWorkspaceId, lastUpdate, onCountsChange]);

    return (
        <dl className="dashboard-summary" aria-label="My fleet flight summary" aria-busy={data.length === 0}>
            {displayCategories.map(({ id, label, countIndex }) => (
                <div
                    key={id}
                    className={`dashboard-card dashboard-summary__item dashboard-flight-panel--${id}${id === 'emergency' && (data[countIndex] ?? 0) > 0 ? ' dashboard-summary__item--attention' : ''}`}>
                    <dt className="dashboard-summary__label">
                        <span className="dashboard-summary__icon" aria-hidden="true">
                            <Icon component={dashboardCategoryIcons[id]} />
                        </span>
                        <span>{label}</span>
                    </dt>
                    <dd className="dashboard-summary__value">{formatCount(data[countIndex])}</dd>
                    {id === 'monitored' && <dd className="dashboard-summary__caption dashboard-summary__caption--active">Active</dd>}
                    {id === 'monitored' && (
                        <dd className="dashboard-summary__breakdown">
                            <dl className="dashboard-summary__statuses">
                                <div className="dashboard-summary__status">
                                    <dt>Planned</dt>
                                    <dd>{formatCount(data[PLANNED_COUNT_INDEX])}</dd>
                                </div>
                            </dl>
                        </dd>
                    )}
                </div>
            ))}
        </dl>
    );
}
